import { err, ok, type Result } from '@src/services/common/Result';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

import type { StoryRepository } from '@src/repositories';
import type { Story } from '@src/types.ts/story';

const logger = LogManager.getLogger('service:ContentAutosave');

export type SavedContentType =
  | 'saved_blog'
  | 'saved_social_post'
  | 'saved_email';

export type SavedContentMap =
  | NonNullable<Story['savedBlog']>
  | NonNullable<Story['savedSocialPost']>
  | NonNullable<Story['savedEmail']>;

type StorySliceForSave = Pick<
  Story,
  'id' | 'savedBlog' | 'savedSocialPost' | 'savedEmail'
>;

export interface AutosaveInPlaceParams {
  story: StorySliceForSave;
  content: string;
  title: string;
  userName: string | null | undefined;
  type: SavedContentType;
  replaceKey: number;
}

export interface SaveFirstParams {
  story: StorySliceForSave;
  content: string;
  title: string;
  userName: string | null | undefined;
  type: SavedContentType;
  replaceKey?: number;
  manuallySaved?: boolean;
}

export interface SaveAiGeneratedParams {
  storyId: string;
  data: unknown;
  type:
    | 'saved_blog'
    | 'saved_social_post'
    | 'saved_email'
    | 'saved_talking_point_content';
}

/**
 * Orchestrates Blog / Social Post / Email autosave against DatoCMS, so view
 * components don't call `storyRepository.*` directly (forbidden per
 * CLAUDE.md's "Components \u2192 Repositories" rule).
 *
 * Pure: no MobX, no store refs, no React. Repository is injected via the
 * constructor so the service can be swapped or stubbed in tests.
 */
export class ContentAutosaveService {
  constructor(private readonly storyRepository: StoryRepository) {}

  /**
   * In-place upsert for an existing saved entry. Never creates a new entry
   * and never touches version history. Wrap every caller edit loop through
   * this path so the debounce stays idempotent.
   */
  async autosaveInPlace(
    params: AutosaveInPlaceParams,
  ): Promise<Result<SavedContentMap>> {
    try {
      const updated = await this.storyRepository.autosaveBlogOrEmail(
        params.story,
        params.content,
        params.title,
        params.userName,
        params.type,
        params.replaceKey,
      );
      if (updated === null) {
        return err(
          'NOT_FOUND',
          `No saved entry for replaceKey ${params.replaceKey}`,
        );
      }
      return ok(updated);
    } catch (e) {
      logger.error('autosaveInPlace failed', extractErrorInfo(e));
      return err('EXTERNAL', 'Failed to autosave content', e);
    }
  }

  /**
   * Create a new entry (or manual save that may replace an existing one).
   * Use on the "no currentContent yet" first-save path and for explicit
   * manual saves.
   */
  async saveFirst(params: SaveFirstParams): Promise<Result<SavedContentMap>> {
    try {
      const updated = await this.storyRepository.saveBlogOrEmail(
        params.story,
        params.content,
        params.title,
        params.userName,
        params.type,
        params.replaceKey,
        params.manuallySaved,
      );
      return ok(updated);
    } catch (e) {
      logger.error('saveFirst failed', extractErrorInfo(e));
      return err('EXTERNAL', 'Failed to save content', e);
    }
  }

  /**
   * Low-level DatoCMS write for a single saved-content field. Used by
   * callers that already have the composed saved-content record and just
   * need to persist it.
   */
  async saveAiGenerated(params: SaveAiGeneratedParams): Promise<Result<void>> {
    try {
      await this.storyRepository.handleSaveAiGeneratedContent(
        params.storyId,
        params.data,
        params.type,
      );
      return ok(undefined);
    } catch (e) {
      logger.error('saveAiGenerated failed', extractErrorInfo(e));
      return err('EXTERNAL', 'Failed to persist AI-generated content', e);
    }
  }
}
